import * as React from 'react';
import {
    Alert, Box, Card, CardContent, Chip, CircularProgress, MenuItem, Pagination, Stack, Table, TableBody, TableCell, TableHead,
    TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionStoreEvents } from '../Api';
import { StoreEventsPageDto } from '../models/dtos/UserPremiumAdminDto';
import { TEST_FILTERS } from './SubscribersCard';

const PAGE_SIZE = 25;

const STORE_FILTERS = [
    { value: 'ALL', label: 'All stores' },
    { value: 'APPLE', label: 'App Store' },
    { value: 'GOOGLE', label: 'Google Play' },
];

const TYPE_LABEL: Record<string, string> = {
    SUBSCRIPTION: 'App purchase',
    SUBSCRIPTION_NOTIFICATION: 'Store notification',
};

const STATUS_COLOR: Record<string, 'success' | 'info' | 'error'> = { SUCCESS: 'success', SKIP: 'info', FAIL: 'error' };

const STATUS_HINT: Record<string, string> = {
    SUCCESS: 'Synced to the subscription',
    SKIP: 'No BETUP account owns this subscription (yet): nothing changed',
    FAIL: 'Refused or failed — the error says why; the store retries a notification',
};

/**
 * Marketing › Subscriptions › Store events (analytics purchaseRequests of the subscription types): every purchase the
 * app reported and every Google / Apple notification, newest first, by type, status, store and real / test (a sandbox
 * environment or the store's test flag), with what came of it (betup-server-api
 * docs/subscriptions.md §14).
 */
export default function SubscriptionStoreEventsCard() {
    const navigate = useNavigate();
    const [type, setType] = React.useState('ALL');
    const [status, setStatus] = React.useState('ALL');
    const [store, setStore] = React.useState('ALL');
    const [test, setTest] = React.useState('ALL');
    const [page, setPage] = React.useState(1);
    const [data, setData] = React.useState<StoreEventsPageDto | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [failed, setFailed] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getSubscriptionStoreEvents(type, status, store, test, page - 1, PAGE_SIZE).then(result => {
            if (cancelled) return;
            setLoading(false);
            setFailed(result == null);
            if (result) setData(result);
        });
        return () => { cancelled = true; };
    }, [type, status, store, test, page]);

    const pages = Math.max(1, Math.ceil((data?.totalItems ?? 0) / PAGE_SIZE));

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack gap={1.5}>
                    <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                        <SyncAltIcon color="primary" />
                        <Typography variant="h6">Store events</Typography>
                        {data && <Chip size="small" variant="outlined" label={data.totalItems} />}
                        {loading && <CircularProgress size={18} />}
                        <Box sx={{ flexGrow: 1 }} />
                        <TextField select size="small" label="Type" value={type} sx={{ width: 190 }}
                            onChange={e => { setType(e.target.value); setPage(1); }}>
                            <MenuItem value="ALL">All</MenuItem>
                            <MenuItem value="SUBSCRIPTION">App purchase</MenuItem>
                            <MenuItem value="SUBSCRIPTION_NOTIFICATION">Store notification</MenuItem>
                        </TextField>
                        <TextField select size="small" label="Status" value={status} sx={{ width: 140 }}
                            onChange={e => { setStatus(e.target.value); setPage(1); }}>
                            {['ALL', 'SUCCESS', 'SKIP', 'FAIL'].map(s => <MenuItem key={s} value={s}>{s === 'ALL' ? 'All' : s}</MenuItem>)}
                        </TextField>
                        <TextField select size="small" label="Store" value={store} sx={{ width: 150 }}
                            onChange={e => { setStore(e.target.value); setPage(1); }}>
                            {STORE_FILTERS.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                        </TextField>
                        <TextField select size="small" label="Payments" value={test} sx={{ width: 140 }}
                            onChange={e => { setTest(e.target.value); setPage(1); }}>
                            {TEST_FILTERS.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                        </TextField>
                    </Stack>
                    {failed && <Alert severity="error">Failed to load the store events.</Alert>}
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>At</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Player</TableCell>
                                <TableCell>Product</TableCell>
                                <TableCell>Environment</TableCell>
                                <TableCell>Store ids</TableCell>
                                <TableCell>Error</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data?.items.length === 0 && (
                                <TableRow><TableCell colSpan={8} align="center">No store events</TableCell></TableRow>
                            )}
                            {data?.items.map(e => (
                                <TableRow key={e.id}>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{e.createdAt ? new Date(e.createdAt).toLocaleString() : '—'}</TableCell>
                                    <TableCell>{e.billingType ? TYPE_LABEL[e.billingType] ?? e.billingType : '—'}</TableCell>
                                    <TableCell>
                                        <Tooltip title={e.status ? STATUS_HINT[e.status] ?? '' : ''}>
                                            <Chip size="small" color={e.status ? STATUS_COLOR[e.status] ?? 'default' : 'default'} label={e.status ?? '—'} />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        {e.userId != null ? (
                                            <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }}
                                                onClick={() => navigate(`/users/${e.userId}?tab=premium`)}>
                                                #{e.userId}
                                            </Typography>
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell>{e.productId ?? '—'}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" gap={0.5} alignItems="center">
                                            <Typography variant="body2">{e.environment ?? '—'}</Typography>
                                            {e.test && <Chip size="small" color="warning" variant="outlined" label="sandbox" />}
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 220 }}>
                                        <Tooltip title={[e.subscriptionKey && `subscription: ${e.subscriptionKey}`, e.paymentId && `payment: ${e.paymentId}`]
                                            .filter(Boolean).join(' · ')}>
                                            <Typography variant="caption" noWrap component="div" sx={{ maxWidth: 220, fontFamily: 'monospace' }}>
                                                {e.paymentId ?? e.subscriptionKey ?? '—'}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 240 }}>
                                        {e.errorMessage ? (
                                            <Tooltip title={e.errorMessage}>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>{e.errorMessage}</Typography>
                                            </Tooltip>
                                        ) : '—'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {pages > 1 && (
                        <Pagination count={pages} page={page} onChange={(_, p) => setPage(p)} sx={{ alignSelf: 'center' }} />
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}
